import React, { useState } from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DataTable } from "../../ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { ChevronDown, ChevronRight, Cpu, HardDrive } from 'lucide-react';
import { Badge } from "../../ui/badge";

// Message interface for the NodesToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Node stats structure
interface NodeStats {
    cpuUtil: number;
    memoryUtil: number;
}

// Node resources structure (extracted from YAML)
interface NodeResources {
    cpuCapacity: string;
    memoryCapacity: string;
}

// Node data structure
interface Node {
    name: string;
    environment: string;
    status: string;
    yaml: string;
    labels: Record<string, string> | null;
    creationTimestamp: number;
    nodeStats: NodeStats;
    resources?: NodeResources;
}

interface NodesResponse {
    nodes: Node[];
}

// Function to format timestamp
const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Function to format memory string to a more readable format (converts Ki to GB)
const formatMemory = (memoryStr: string): string => {
    if (!memoryStr) return "N/A";
    
    // Handle memory in Ki format
    if (memoryStr.endsWith('Ki')) {
        const kiBytes = parseInt(memoryStr.replace('Ki', ''));
        const gbValue = kiBytes / (1024 * 1024);
        return `${gbValue.toFixed(1)} GB`;
    }
    
    return memoryStr;
};

// Parse YAML content to extract CPU and memory capacity
const extractResourcesFromYaml = (yamlContent: string): NodeResources => {
    const resources: NodeResources = {
        cpuCapacity: 'N/A',
        memoryCapacity: 'N/A'
    };
    
    try {
        // Look for CPU capacity with a simple regex
        const cpuMatch = yamlContent.match(/capacity:\s+cpu:\s+"?([^"\s]+)"?/);
        if (cpuMatch && cpuMatch[1]) {
            resources.cpuCapacity = cpuMatch[1];
        }
        
        // Look for memory capacity with an improved regex
        const memoryMatch = yamlContent.match(/capacity:[\s\S]*?memory:\s+(\d+[A-Za-z]+)/);
        if (memoryMatch && memoryMatch[1]) {
            resources.memoryCapacity = memoryMatch[1];
        }
    } catch (error) {
        console.error('Error parsing resources from YAML:', error);
    }
    
    return resources;
};

// Tool renderer for nodes data
export function NodesToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // We only want to render the response, not the call
    if (!message.tool_call_response_content) {
        // This is a tool call, do the default rendering
        return <DefaultToolCallRenderer message={message} />;
    }

    let response: NodesResponse | null = null;
    try {
        response = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing nodes response data:', error);
        return null;
    }

    // Validate response
    if (!response || !response.nodes || !Array.isArray(response.nodes)) {
        return null;
    }
    
    // Extract resource information from YAML
    const enhancedNodes = response.nodes.map(node => {
        const resources = extractResourcesFromYaml(node.yaml);
        return {
            ...node,
            resources
        };
    });

    // Define columns for the DataTable
    const columns: ColumnDef<Node>[] = [
        {
            id: "name",
            accessorKey: "name",
            header: "Node Name",
            cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
        },
        {
            id: "status",
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => (
                <Badge 
                    className={
                        row.original.status === "Running" 
                            ? "bg-green-500/20 text-green-500 hover:bg-green-500/20" 
                            : "bg-orange-500/20 text-orange-500 hover:bg-orange-500/20"
                    }
                >
                    {row.original.status}
                </Badge>
            ),
        },
        {
            id: "capacity",
            header: "Capacity",
            cell: ({ row }) => (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                        <Cpu className="h-3 w-3" /> 
                        <div className="text-sm">{row.original.resources?.cpuCapacity || 'N/A'}</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <HardDrive className="h-3 w-3" /> 
                        <div className="text-sm">{formatMemory(row.original.resources?.memoryCapacity || '')}</div>
                    </div>
                </div>
            ),
        },
        {
            id: "stats",
            header: "Resource Usage",
            cell: ({ row }) => (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                        <Cpu className="h-3 w-3" /> 
                        <div className="text-sm">{row.original.nodeStats?.cpuUtil?.toFixed(1)}%</div>
                    </div>
                    <div className="flex items-center gap-1">
                        <HardDrive className="h-3 w-3" /> 
                        <div className="text-sm">{row.original.nodeStats?.memoryUtil?.toFixed(1)}%</div>
                    </div>
                </div>
            ),
        },
        {
            id: "environment",
            accessorKey: "environment",
            header: "Environment",
            cell: ({ row }) => row.original.environment,
        },
        {
            id: "created",
            accessorKey: "creationTimestamp",
            header: "Created",
            cell: ({ row }) => formatTimestamp(row.original.creationTimestamp),
        },
        {
            id: "details",
            header: "Details",
            cell: ({ row }) => {
                const [isOpen, setIsOpen] = useState(false);
                
                return (
                    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
                        <CollapsibleTrigger className="flex items-center gap-2 text-sm text-textmedium hover:text-textlight transition-colors">
                            {isOpen ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}
                            <span>YAML</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="mt-2 bg-[#1D2739] rounded-lg p-4 text-sm font-mono text-textmedium overflow-x-auto max-h-96">
                                <pre className="whitespace-pre-wrap">{row.original.yaml}</pre>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                );
            }
        }
    ];

    // No filters for this table
    const filters: { filteringColumn: string; title: string; }[] = [];

    return (
        <div className="w-full border rounded">
            <DataTable
                columns={columns}
                data={enhancedNodes}
                tableTitle="Kubernetes Nodes"
                filters={filters}
                showPagination={true}
                removeBorder={true}
                textFilteringColumnName="name"
                textFilteringPlaceholder="Search nodes..."
                columnConfigs={[
                    { id: "name", isFilterable: true },
                    { id: "environment", isFilterable: true }
                ]}
            />
        </div>
    );
}

// Static method to determine if this renderer should render a message
NodesToolRenderer.shouldRender = (message: Message): boolean => {
    return true;
};